import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { opportunityService, adminService } from '../services/api';

const STAGES = [
  'Discovery Session',
  'Qualified',
  'Proposal Validation - Tech & Business',
  'FR Submission',
  'PO Recieved',
  'Delivery Implementation',
  'Customer Sign Off',
  'Cash Claim/Invoice Raised',
  'Launched',
];

const STAGE_COLOR = {
  'Discovery Session': 'col-new',
  'Qualified': 'col-qual',
  'Proposal Validation - Tech & Business': 'col-proposal',
  'FR Submission': 'col-neg',
  'PO Recieved': 'col-neg',
  'Delivery Implementation': 'col-proposal',
  'Customer Sign Off': 'col-qual',
  'Cash Claim/Invoice Raised': 'col-won',
  'Launched': 'col-won',
};

function formatUSD(n) {
  if (!n && n !== 0) return '—';
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + (n / 1000).toFixed(0) + 'K';
  return '$' + Number(n).toLocaleString();
}

export default function PipelineView({ isCKAdmin = false }) {
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterPartner, setFilterPartner] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    const fetch = isCKAdmin
      ? adminService.getAllOpportunities({ limit: 1000 }).then(d => d.opportunities || [])
      : opportunityService.getAll().then(d => d.opportunities || []);
    fetch.then(setOpportunities).catch(console.error).finally(() => setLoading(false));
  }, [isCKAdmin]);

  const partners = useMemo(
    () => [...new Set(opportunities.map(o => o.partner_company).filter(Boolean))].sort(),
    [opportunities]
  );

  const filtered = useMemo(() => {
    let r = opportunities;
    if (filterPartner) r = r.filter(o => o.partner_company === filterPartner);
    if (filterStatus) r = r.filter(o => o.status === filterStatus);
    return r;
  }, [opportunities, filterPartner, filterStatus]);

  const byStage = useMemo(() => {
    const map = {};
    STAGES.forEach(s => { map[s] = []; });
    filtered.forEach(o => {
      const stage = STAGES.includes(o.deal_stage) ? o.deal_stage : 'Discovery Session';
      map[stage].push(o);
    });
    return map;
  }, [filtered]);

  const totalARR = filtered.reduce((s, o) => s + (parseFloat(o.arr_usd) || 0), 0);

  if (loading) return <div className="loading">Loading pipeline…</div>;

  return (
    <div className="pipeline-view">
      <div className="pipeline-header">
        <div>
          <h2>Pipeline</h2>
          <span className="pipeline-summary">
            {filtered.length} deal{filtered.length !== 1 ? 's' : ''} · {formatUSD(totalARR)} total ARR
          </span>
        </div>
        <div className="pipeline-filters">
          {isCKAdmin && partners.length > 0 && (
            <select value={filterPartner} onChange={e => setFilterPartner(e.target.value)} className="pipeline-select">
              <option value="">All Partners</option>
              {partners.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          )}
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="pipeline-select">
            <option value="">All Status</option>
            <option value="submitted">Submitted</option>
            <option value="in_review">In Review</option>
            <option value="approved">Approved</option>
            <option value="active">Active</option>
          </select>
          {(filterPartner || filterStatus) && (
            <button className="clear-filters-btn" onClick={() => { setFilterPartner(''); setFilterStatus(''); }}>
              Clear
            </button>
          )}
        </div>
      </div>

      <div className="kanban-board">
        {STAGES.map(stage => {
          const cards = byStage[stage] || [];
          const stageARR = cards.reduce((s, o) => s + (parseFloat(o.arr_usd) || 0), 0);
          return (
            <div key={stage} className={`kanban-col ${STAGE_COLOR[stage] || ''}`}>
              <div className="kanban-col-header">
                <span className="kanban-stage-name">{stage}</span>
                <div className="kanban-stage-meta">
                  <span className="kanban-count">{cards.length}</span>
                  {stageARR > 0 && <span className="kanban-arr">{formatUSD(stageARR)}</span>}
                </div>
              </div>
              <div className="kanban-cards">
                {cards.map(opp => (
                  <div
                    key={opp.opportunity_id}
                    className="kanban-card"
                    onClick={() => navigate(`/opportunities/${opp.opportunity_id}`)}
                  >
                    {opp.cpn_id && <div className="kanban-card-id">{opp.cpn_id}</div>}
                    <div className="kanban-card-name">{opp.opportunity_name}</div>
                    <div className="kanban-card-company">{opp.company_name}</div>
                    {isCKAdmin && opp.partner_company && (
                      <div className="kanban-card-partner">{opp.partner_company}</div>
                    )}
                    <div className="kanban-card-footer">
                      <span className="kanban-card-arr">{formatUSD(opp.arr_usd)}</span>
                      <span className={`badge badge-${opp.status}`}>{(opp.status || '').replace('_', ' ')}</span>
                    </div>
                    {opp.estimated_close_date && (
                      <div className="kanban-card-date">Close: {opp.estimated_close_date}</div>
                    )}
                  </div>
                ))}
                {cards.length === 0 && (
                  <div className="kanban-empty">No deals</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
